import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
} from '@mui/material';
import type { FormikHelpers } from 'formik';
import { FieldArray, Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import ContentLoader from '@components/ContentLoader';
import IconButton from '@components/IconButton';
import RoundedButton from '@components/RoundedButton/index';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import type { OnboardingUser } from '@models/onboarding-member';

import { useAdminListRoles } from 'generated/admin.generated';
import type { AccountType } from 'generated/types';
import { useEmailExistsLazyQuery } from 'generated/user.generated';

import NewUserForm from './NewUserForm';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  add: {
    id: 'newAccountForm.add',
    defaultMessage: 'Add another user',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  emailTaken: {
    id: 'newAccountForm.emailTaken',
    defaultMessage: 'Email taken',
  },
  mustHaveMembers: {
    id: 'newAccountForm.mustHaveMembers',
    defaultMessage: 'Must have members',
  },
  minimumMembers: {
    id: 'newAccountForm.minimumMembers',
    defaultMessage: 'Minimum of {number} members',
  },
  remove: {
    id: 'newAccountForm.remove',
    defaultMessage: 'Remove User',
  },
  user: {
    id: 'newAccountForm.user',
    defaultMessage: 'User #{number}',
  },
  additionalUser: {
    id: 'newAccountForm.additionalUser',
    defaultMessage: 'User Details',
  },
});

const defaultUser: OnboardingUser = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  roles: [],
};

interface NewUserFormCollectionProps {
  initialMembers: OnboardingUser[];
  onSubmit?: (values: { members: OnboardingUser[] }) => void;
  onBack?: () => void;
  maxUsers?: number;
  isAddForm?: boolean;
  /** Account type of the users being added */
  accountType?: AccountType;
}

const NewUserFormCollection = ({
  initialMembers = [],
  onSubmit,
  onBack,
  maxUsers = 0,
  isAddForm = false,
  accountType,
}: NewUserFormCollectionProps) => {
  const { formatMessage } = useIntl();
  const { emailSchema } = useCommonSchemas();
  const [emailExists] = useEmailExistsLazyQuery();
  const { data: rolesData, loading: rolesLoading } = useAdminListRoles();

  const roleOptions = (rolesData?.listRoles ?? [])
    .filter(({ scope }) => !accountType || scope === accountType)
    .map((role) => ({
      label: role.role_name,
      value: role.role_id,
    }));

  // Validation schema
  const planMembersSchema = Yup.object().shape({
    members: Yup.array()
      .of(
        Yup.object().shape({
          firstName: Yup.string().required(
            formatMessage(messages.requiredText)
          ),
          middleName: Yup.string(),
          lastName: Yup.string().required(formatMessage(messages.requiredText)),
          email: emailSchema.required(formatMessage(messages.requiredText)),
        })
      )
      .required(formatMessage(messages.mustHaveMembers))
      .min(1, formatMessage(messages.minimumMembers, { number: 1 })),
  });

  async function handleSubmit(
    values: { members: OnboardingUser[] },
    { setSubmitting, setErrors }: FormikHelpers<{ members: OnboardingUser[] }>
  ) {
    // Check emails
    const responses = await Promise.all(
      values.members.map(async ({ email }) => {
        if (email) {
          const result = await emailExists({ variables: { input: { email } } });
          return result.data?.emailExists;
        }
        return false;
      })
    );

    // Email message
    if (responses.some((resp) => resp)) {
      setErrors({
        members: responses.map((resp) => ({
          email: resp ? formatMessage(messages.emailTaken) : '',
        })),
      });

      setSubmitting(false);
    } else if (onSubmit) {
      onSubmit(values);
    } else {
      setSubmitting(false);
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        members: initialMembers,
      }}
      onSubmit={handleSubmit}
      validationSchema={planMembersSchema}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form>
            <FieldArray
              name="members"
              render={(arrayHelpers) => {
                const handleAddUser = () => {
                  const numUsers = values.members.length;
                  if (numUsers < maxUsers || maxUsers === 0) {
                    arrayHelpers.push({ ...defaultUser });
                  }
                };

                const handleRemoveUser = (index: number) => {
                  return arrayHelpers.remove(index);
                };

                return (
                  <Stack spacing={1}>
                    {values.members.map((_member, index) => (
                      <Card key={index}>
                        <CardHeader
                          title={formatMessage(
                            messages[isAddForm ? 'additionalUser' : 'user'],
                            {
                              number: index + 1,
                            }
                          )}
                          action={
                            index > 0 && (
                              <IconButton
                                iconType="fad"
                                name="trash"
                                tooltip={formatMessage(messages.remove)}
                                onClick={() => handleRemoveUser(index)}
                              />
                            )
                          }
                        />
                        <Divider />
                        <CardContent>
                          <ContentLoader loading={rolesLoading}>
                            <NewUserForm
                              roleOptions={roleOptions}
                              index={index}
                            />
                          </ContentLoader>
                        </CardContent>
                      </Card>
                    ))}
                    <Card>
                      <CardContent>
                        {(!maxUsers || values.members.length < maxUsers) && (
                          <RoundedButton onClick={handleAddUser} type="button">
                            {formatMessage(messages.add)}
                          </RoundedButton>
                        )}
                      </CardContent>
                      <CardActions>
                        <RoundedButton
                          disabled={isSubmitting}
                          onClick={onBack}
                          type="button"
                        >
                          {formatMessage(messages.back)}
                        </RoundedButton>
                        <RoundedButton disabled={isSubmitting} type="submit">
                          {formatMessage(messages.submit)}
                        </RoundedButton>
                      </CardActions>
                    </Card>
                  </Stack>
                );
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default NewUserFormCollection;
export { defaultUser };
